import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaWallet, FaPercentage, FaMoneyBillWave, FaUsers, FaEye, FaEyeSlash, FaHistory } from 'react-icons/fa';
import { getVendorWalletSummary } from '../../features/vendor/vendorWalletSlice';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';

const VendorWalletDisplay = ({ vendorId, onNavigateToWallet }) => {
  const dispatch = useDispatch();
  const [showBalances, setShowBalances] = useState(true);
  const [commissionData, setCommissionData] = useState(null);
  const [loadingCommission, setLoadingCommission] = useState(true);

  const {
    driverWallets,
    totalWalletBalance,
    totalCommissionEarned,
    loading,
    error,
  } = useSelector((state) => state.vendorWallet);

  useEffect(() => {
    if (vendorId) {
      dispatch(getVendorWalletSummary(vendorId));
      fetchCommissionData();
    }
  }, [dispatch, vendorId]);

  // Auto-refresh on commission-rate updates from other components/tabs
  useEffect(() => {
    const onCustom = (e) => {
      if (!e?.detail || e.detail.vendorId !== vendorId) return;
      dispatch(getVendorWalletSummary(vendorId));
      fetchCommissionData();
    };
    const onStorage = (e) => {
      if (e.key !== 'vendorCommissionRateUpdated') return;
      try {
        const data = JSON.parse(e.newValue || '{}');
        if (data.vendorId === vendorId) {
          dispatch(getVendorWalletSummary(vendorId));
          fetchCommissionData();
        }
      } catch {}
    };
    window.addEventListener('vendor-commission-updated', onCustom);
    window.addEventListener('storage', onStorage);
    // Also periodic refresh as safety
    const interval = setInterval(() => {
      dispatch(getVendorWalletSummary(vendorId));
    }, 60000);
    return () => {
      window.removeEventListener('vendor-commission-updated', onCustom);
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, [dispatch, vendorId]);

  const fetchCommissionData = async () => {
    try {
      const token = localStorage.getItem('vendorToken');

      if (!token) {
        setLoadingCommission(false);
        return;
      }

      // Try to get real commission data from vendor settings first
      let response = await axios.get(
        `${API_URL}/vendor/${vendorId}/commission/settings`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success || response.data.status) {
        setCommissionData(response.data.data);

      } else {

        // Fallback: Get admin commission overview for real admin rates
        try {
          const adminResponse = await axios.get(
            `${API_URL}/commission/admin/overview`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          if (adminResponse.data.success) {
            const adminData = adminResponse.data.data;
            const globalRate = adminData.global?.percentage || 10;

            // Create structure with real admin rate and current vendor rate
            setCommissionData({
              commissionStructure: {
                adminRate: globalRate,
                vendorRate: 15,
                driverRate: 100 - globalRate - 15
              },
              commissionSettings: {
                effectiveRate: 15,
                isUsingGlobal: true
              }
            });


            toast.info('ℹ️ Commission data loaded from admin global settings', {
              position: 'bottom-right',
              autoClose: 3000
            });
          }
        } catch (adminError) {
          console.error('❌ Error fetching admin commission data:', adminError);
          toast.error('⚠️ Could not load real commission data', {
            position: 'bottom-right',
            autoClose: 5000
          });
        }
      }
    } catch (error) {
      console.error('❌ Error fetching commission data:', error);
      toast.error('⚠️ Failed to load commission data from admin system', {
        position: 'bottom-right',
        autoClose: 5000
      });
    } finally {
      setLoadingCommission(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Use real commission data from API - no hardcoded fallbacks
  const getCommissionRates = () => {
    if (!commissionData) {
      // Loading state - return minimal values
      return {
        vendorRate: 0,
        adminRate: 0,
        driverRate: 0
      };
    }

    const { commissionStructure, commissionSettings } = commissionData;

    // Use real rates from API response
    const vendorRate = commissionSettings?.effectiveRate || commissionStructure?.vendorRate || 0;
    const adminRate = commissionStructure?.adminRate || 0;
    const driverRate = commissionStructure?.driverRate || (100 - adminRate - vendorRate);


    return {
      vendorRate,
      adminRate,
      driverRate
    };
  };

  const rates = getCommissionRates();
  const walletData = {
    commissionRate: rates.vendorRate,
    totalWalletBalance: totalWalletBalance || 0,
    driversCount: driverWallets?.length || 0,
    adminRate: rates.adminRate,
    driverRate: rates.driverRate
  };

  if (loading || loadingCommission) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-indigo-100 rounded-xl shadow-lg p-6 border border-purple-200 animate-pulse">
        <div className="h-6 w-40 bg-gray-200 rounded mb-4"></div>
        <div className="h-20 bg-gray-200 rounded mb-4"></div>
        <div className="h-16 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-100 rounded-xl shadow-lg p-6 border border-purple-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-3">
            <FaWallet className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Vendor Wallet</h3>
            <p className="text-sm text-gray-600">
              Commission Rate: {walletData.commissionRate}% per ride
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowBalances(!showBalances)}
          className="p-2 rounded-full hover:bg-purple-200 transition-colors"
          title={showBalances ? "Hide balances" : "Show balances"}
        >
          {showBalances ? (
            <FaEye className="w-4 h-4 text-purple-600" />
          ) : (
            <FaEyeSlash className="w-4 h-4 text-purple-600" />
          )}
        </button>
      </div>

      {/* Main Wallet Stats */}
      <div className="mb-6">
        {/* Total Wallet Balance - Shows Commission Earned Amount */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Total Wallet Balance</span>
            <FaWallet className="w-4 h-4 text-blue-500" />
          </div>
          <div className="flex items-center">
            <span className="text-3xl font-bold text-blue-600">
              {showBalances ? formatCurrency(totalCommissionEarned || 0) : "₹****"}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Across {walletData.driversCount} drivers
          </p>
        </div>
      </div>

      {/* Commission Rate Highlight */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200 mb-6">
        <div className="text-center">
          <div className="text-4xl font-bold text-green-900 mb-2">
            {walletData.commissionRate}%
          </div>
          <p className="text-lg text-green-700 font-medium">Per completed ride</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={() => onNavigateToWallet && onNavigateToWallet('wallet')}
          className="bg-purple-500 hover:bg-purple-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
        >
          <FaWallet className="mr-2" />
          Wallet Details
        </button>

        <button
          onClick={() => onNavigateToWallet && onNavigateToWallet('commission')}
          className="bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
        >
          <FaMoneyBillWave className="mr-2" />
          Commission
        </button>

        <button
          onClick={() => window.location.href = '/vendor/wallet/history'}
          className="bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
        >
          <FaHistory className="mr-2" />
          History
        </button>
      </div>

      {/* Commission Structure Info - Using Real Data */}
      <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
        <h4 className="text-sm font-medium text-purple-800 mb-2">Commission Structure</h4>
        <div className="text-xs text-purple-700 space-y-1">
          <div className="flex justify-between">
            <span>Admin Commission:</span>
            <span className="font-medium">{walletData.adminRate}%</span>
          </div>
          <div className="flex justify-between">
            <span>Vendor Commission (You):</span>
            <span className="font-medium text-purple-900">{walletData.commissionRate}%</span>
          </div>
          <div className="flex justify-between">
            <span>Driver Gets:</span>
            <span className="font-medium">{walletData.driverRate}%</span>
          </div>
        </div>
        {commissionData && (
          <div className="mt-2 pt-2 border-t border-purple-200">
            <div className="text-xs text-purple-600">
              ✅ Real Data Source: {commissionData.commissionSettings?.isUsingGlobal ? 'Admin Global Settings' : 'Vendor Specific Settings'}
            </div>
            <div className="text-xs text-purple-500 mt-1">
              Updated from live admin system (not hardcoded)
            </div>
          </div>
        )}
      </div>

      {/* Last Updated */}
      <p className="text-xs text-gray-500 mt-3 text-center">
        Last updated: {new Date().toLocaleTimeString()}
      </p>
    </div>
  );
};

export default VendorWalletDisplay;
